import React, { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import {
  fetchConversations,
  ConversationSummary,
} from "apps/user/learner/api/coach-history";
import { ActiveQaThread } from "apps/user/learner/hooks/use-ori-qa-session";
import { QaAgenda } from "apps/user/learner/api/coach-qa";
import { agendaLabel } from "apps/user/learner/lib/ori-agenda-label";
import { logError } from "utility/Function";
import { useTranslation } from "lib/i18n/use-translation";
import styles from "./OriQaThreads.module.scss";

// One row's worth of thread: a thread is a sequence of conversations (Ori opens
// a new one each time it moves agenda), so everything a row shows is derived
// across the whole sequence rather than read off one segment.
interface QaThreadRow {
  threadId: string;
  // The newest segment — resuming a thread must land where it actually is.
  conversationId: string;
  title: string | null;
  // Agendas oldest → newest, consecutive repeats collapsed.
  agendaTypes: string[];
  startedAt: string;
  messageCount: number;
}

const groupByThread = (conversations: ConversationSummary[]): QaThreadRow[] => {
  const byThread = new Map<string, ConversationSummary[]>();
  conversations.forEach((conversation) => {
    const threadId = conversation.thread?.id;
    if (!threadId) return;
    const segments = byThread.get(threadId);
    if (segments) segments.push(conversation);
    else byThread.set(threadId, [conversation]);
  });

  return Array.from(byThread.entries())
    .map(([threadId, segments]) => {
      const ordered = [...segments].sort(
        (a, b) => Date.parse(a.started_at) - Date.parse(b.started_at)
      );
      const latest = ordered[ordered.length - 1];
      return {
        threadId,
        conversationId: latest.id,
        // Newest title that exists — a moved segment can be untitled while the
        // thread already has a name worth showing.
        title: ordered.reduce<string | null>(
          (named, segment) => segment.title ?? named,
          null
        ),
        agendaTypes: ordered
          .map((segment) => segment.conversation_type)
          .filter((type, index, all) => type !== all[index - 1]),
        startedAt: latest.started_at,
        messageCount: ordered.reduce(
          (total, segment) => total + segment.message_count,
          0
        ),
      };
    })
    .sort((a, b) => Date.parse(b.startedAt) - Date.parse(a.startedAt));
};

// How many agendas a trail shows before the middle collapses. The origin and
// where the thread is now are what a tester reads; keep it to one line.
const TRAIL_MAX = 3;

const agendaTrail = (agendaTypes: string[], agendas: QaAgenda[]): string => {
  const names = agendaTypes.map((type) => agendaLabel(type, agendas));
  const shown =
    names.length > TRAIL_MAX ? [names[0], "…", names[names.length - 1]] : names;
  return shown.join(" → ");
};

interface OriQaThreadsProps {
  configId: string;
  // The agendas the Configuration serves, for naming each thread's agenda.
  agendas: QaAgenda[];
  // Bumped by the parent (after a new thread or an archive) to refetch.
  refreshKey: number;
  // Resume a past thread — load it live into the chat view.
  onContinueThread: (thread: ActiveQaThread) => void;
  // The thread currently open in the chat, for highlighting.
  activeThreadId: string | null;
}

// Lists the caller's bounded QA threads for one Configuration — one row per
// thread, not per conversation. Archived threads are excluded server-side, so
// every row here is resumable. Clicking a thread continues it live in the chat
// view, from its newest segment. Several threads for one Configuration
// coexist — this is how you switch between them.
const OriQaThreads: React.FC<OriQaThreadsProps> = ({
  configId,
  agendas,
  refreshKey,
  onContinueThread,
  activeThreadId,
}) => {
  const { t } = useTranslation(["ori"]);
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setLoadError(false);

    (async () => {
      try {
        const result = await fetchConversations({ lifecycle: "bounded" });
        if (cancelled) return;
        // The endpoint returns all of the caller's bounded threads; scope to
        // the Configuration under test via the thread's configuration_id.
        setConversations(
          result.conversations.filter(
            (conversation) => conversation.thread?.configuration_id === configId
          )
        );
      } catch (error) {
        if (cancelled) return;
        logError("Ori QA threads fetch failed", error);
        setLoadError(true);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [configId, refreshKey]);

  const rows = useMemo(() => groupByThread(conversations), [conversations]);

  return (
    <div className={styles.threads} data-testid="ori-qa-threads">
      <span className={styles.heading}>{t("ori:qa.threads.title")}</span>

      {isLoading ? (
        <p className={styles.muted}>{t("ori:qa.threads.loading")}</p>
      ) : loadError ? (
        <p className={styles.error} data-testid="ori-qa-threads-error">
          {t("ori:qa.threads.loadError")}
        </p>
      ) : rows.length === 0 ? (
        <p className={styles.muted} data-testid="ori-qa-threads-empty">
          {t("ori:qa.threads.empty")}
        </p>
      ) : (
        <ul className={styles.list}>
          {rows.map((row) => {
            const isActive = row.threadId === activeThreadId;
            const hasMoved = row.agendaTypes.length > 1;
            return (
              <li key={row.threadId} className={styles.item}>
                <button
                  type="button"
                  className={clsx(
                    styles.threadButton,
                    isActive && styles.threadButtonActive
                  )}
                  data-testid="ori-qa-thread"
                  aria-current={isActive}
                  onClick={() =>
                    onContinueThread({
                      threadId: row.threadId,
                      conversationId: row.conversationId,
                    })
                  }
                >
                  <span className={styles.threadTitle}>
                    {row.title || t("ori:qa.threads.untitled")}
                  </span>
                  <span
                    className={styles.threadMeta}
                    data-testid="ori-qa-thread-agenda"
                  >
                    {t("ori:qa.threads.agenda", {
                      agenda: agendaLabel(
                        row.agendaTypes[row.agendaTypes.length - 1],
                        agendas
                      ),
                    })}
                  </span>
                  {hasMoved && (
                    <span
                      className={clsx(styles.threadMeta, styles.threadTrail)}
                      data-testid="ori-qa-thread-trail"
                    >
                      {t("ori:qa.threads.trail", {
                        trail: agendaTrail(row.agendaTypes, agendas),
                      })}
                    </span>
                  )}
                  <span className={styles.threadMeta}>
                    {t(
                      isActive
                        ? "ori:qa.threads.metaOpen"
                        : "ori:qa.threads.meta",
                      {
                        date: new Date(row.startedAt).toLocaleString(),
                        messageCount: row.messageCount,
                      }
                    )}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default OriQaThreads;
